<template lang="pug">
  .buy-in-store(v-if="!hideSection")
    #pear-pdp-target
</template>

<script>
  import axios from 'axios';
import Vue from 'vue';
import Icon from 'scripts/components/basic/Icon.vue';
import IconButton from 'scripts/components/buttons/IconButton.vue';
import VButton from 'scripts/components/buttons/VButton.vue';
import DropdownWithToggles from 'scripts/components/forms/DropdownWithToggles.vue';
import NumberInput from 'scripts/components/forms/NumberInput.vue';
import LocalStoreLocation from 'scripts/components/product/LocalStoreLocation.vue';
import ProductLocationFilterSlideout from 'scripts/components/product/ProductLocationFilterSlideout.vue';
import ProductZipModal from 'scripts/components/product/ProductZipModal.vue';
import { handleize } from 'scripts/filters/string.js';

  export default {
    components: {
      Icon,
      IconButton,
      DropdownWithToggles,
      NumberInput,
      ProductZipModal,
      ProductLocationFilterSlideout,
      LocalStoreLocation,
      VButton,
    },
    props: {
      hideSection: Boolean,
      product: Object,
      variant: Object,
      zipModalHeading: String,
      zipModalBody: String,
    },
    data() {
      return {
        localStoreData: {},
        localStores: [],
        localZipcode: null,
        localZipcodeOverride: null,
        displayedStoreLimit: 3,
        errorThrown: false,
        isLoading: false,
        selectedStores: {
          'Target': false,
          'Walmart': false,
          'Kroger': false,
          'Ralphs': false,
          'Vons': false,
          'Giant': false,
          'Hannaford': false,
          'Instacart': false,
          'Stop & Shop': false,
        },
        nonHyphenStores: [
          'Giant',
          'Hannaford',
          'Instacart',
          'Stop & Shop',
        ],
        targetPID: '',
        walmartPID: '',
        krogerPID: '',
        nonHyphenStoreUrls: {
          'Giant': '',
          'Hannaford': '',
          'Instacart': '',
          'Stop & Shop': '',
        },
        targetUrl: '',
        walmartUrl: '',
        instacartUrl: '',
        krogerUrl: '',
        giantUrl: '',
        hannafordUrl: '',
        stopShopUrl: '',
      }
    },
    computed: {
      localZipcodeText() {
        return this.localZipcode == null ? 'Select your Zipcode' : `You're in ${this.localZipcode} area`
      },
      zipModalToggleText() {
        return this.localZipcode == null ? 'Select' : 'Change'
      },
      displayStoreToggleText() {
        return this.displayedStoreLimit === 3 ? 'Show More' : 'Show Less'
      },
      filteredSelectedStores() {
        // * List of hyphen stores combined with list of non-hyphen stores with non-blank store URLs input (taken from metafields)

        let hyphenStores = [];

        const variant = this.variant || this.product.variants[0];
        if (variant.kroger_pid || variant.kroger_url) { hyphenStores.push('Kroger') }

        if (variant.target_pid || variant.target_url) { hyphenStores.push('Target') }

        if (variant.walmart_pid && variant.walmart_url) { hyphenStores.push('Walmart') }

        const availableStoreList = Object.keys(this.filteredNonHyphenStoreUrls).concat(hyphenStores);

        // * Return an object of values to fi
        let filteredDropdown = Object.entries(this.selectedStores).reduce((filteredStores, [key, val]) => {
          if (availableStoreList.includes(key)) filteredStores[key] = val
          return filteredStores
        }, {})

        return filteredDropdown
      },
      filteredNonHyphenStoreUrls() {
        // * Return an array of nonHyphenStores that have non-null entries
        return Object.entries(this.nonHyphenStoreUrls).reduce((filteredUrls, [key, val]) => {
          if (this.nonHyphenStoreUrls[key] !== null) filteredUrls[key] = val
          return filteredUrls
        }, {});
      },
      filteredLocalStores() {
        let selectedFilters = Object.entries(this.filteredSelectedStores).reduce((filteredStores, [key, val]) => {
          if (val) filteredStores.push(key.toLowerCase());
          return filteredStores
        }, [])

        let filteredStores;

        // * Append the basic store links if they exist
        if (selectedFilters.length > 0) {
          filteredStores = this.localStores.filter(store => selectedFilters.includes(store.retailerName));
          for (const store in this.filteredNonHyphenStoreUrls) {
            const filterHandles = selectedFilters.map(filter => handleize(filter));
            if (filterHandles.includes(handleize(store))) {
              filteredStores.push({
                "shopNowUrl": this.nonHyphenStoreUrls[store],
                "retailerLogo": "",
                "retailerName": store,
                "retailerAddress": "",
                "availability": 'hidden',
                "price": "",
              })
            }
          }
        } else {
          //* No filters applied so we display all stores
          filteredStores = [...this.localStores];
          for (const store in this.filteredNonHyphenStoreUrls) {
            if (this.filteredNonHyphenStoreUrls[store] !== null) {
              filteredStores.push({
                "shopNowUrl": this.nonHyphenStoreUrls[store],
                "retailerLogo": "",
                "retailerName": store,
                "retailerAddress": "",
                "availability": 'hidden',
                "price": "",
              })
            }
          }
        }
        return filteredStores
      }
    },
    methods: {
      toggleZipModal() {
        this.$refs.zipModal.open();
      },
      toggleMobileLocationFilters() {
        this.$refs.mobileLocationFilters.open();
      },
      updateLocalZipcode() {
        this.localZipcode = this.localZipcodeOverride || this.localZipcode;
        localStorage.setItem('ratio-zip', this.localZipcode);
        this.$refs.zipModal.close();
      },
      toggleDisplayedStores() {
        this.displayedStoreLimit === 3 ? this.displayedStoreLimit = 10 : this.displayedStoreLimit = 3;
      },
      async getLocalZipcode() {
        const savedZipcode = localStorage.getItem('ratio-zip');
        if (savedZipcode) {
          this.localZipcode = savedZipcode;
        } else {
          const res = await axios.get('https://shadowfax.intrc.net/api/geoip/');
          this.localZipcode = res.data.data.postal_code;
        }
      },
      updateVariantInfo(variant) {
        // Update PIDs
        this.targetPID = variant.target_pid;
        this.krogerPID = variant.kroger_pid;
        this.walmartPID = variant.walmart_pid;

        // Update Vendor URLs
        this.targetUrl = variant.target_url;
        this.walmartUrl = variant.walmart_url;
        this.instacartUrl = variant.instacart_url;
        this.krogerUrl = variant.kroger_url;
        this.giantUrl = variant.giant_url;
        this.hannafordUrl = variant.hannaford_url;
        this.stopShopUrl = variant.stopandshop_url;

        this.nonHyphenStoreUrls = {
          'Target': variant.target_url,
          'Walmart': variant.walmart_url,
          'Instacart': variant.instacart_url,
          'Kroger': variant.kroger_url,
          'Giant': variant.giant_url,
          'Hannaford': variant.hannaford_url,
          'Stop & Shop': variant.stopandshop_url
        }

        // Refresh Pear Frame
        const pearFrame = document.querySelector('#pear-pdp-target iframe');
        if (pearFrame) {
          fetch('https://offers.pearcommerce.com/js/sdk/ratio-food-pdp.js').then((res) => res.text()).then((res) => {
            let s = res.indexOf(window.location.pathname + window.location.search);
            if (s < 0)
              s = res.indexOf(window.location.pathname);
            if (s > 0) {
              const e = res.indexOf('"offerId": "', s) + '"offerId": "'.length;
              const offerId = res.substring(e, res.indexOf('"', e));
              pearFrame.src = pearFrame.src.replace(/agnostic-pdp-label\/\d+\//, `agnostic-pdp-label/${offerId}/`).replace(/id%3D\d+$/, `id%3D${variant.id}`);
            }
          });
        }
      },
      handleError() {
        this.errorThrown = true;
      },
      async getLocalStoreData(zipcode) {
        const zip = zipcode || this.localZipcode;
        const params = {
          format: "vnd.api+json",
          zip: zip
        }

        if (this.krogerPID) params.kroger_sku = this.krogerPID;
        if (this.targetPID) params.target_sku = this.targetPID;
        if (this.walmartPID) params.walmart_sku = this.walmartPID;

        // GET /api/where-to-buy/?kroger_sku=0001600016435&walmart_sku=537725846

        // ? Should we persist an initial request response somewhere to reduce excessive requests?
        try {
          this.isLoading = true;
          const res = await axios.get('https://shadowfax.intrc.net/api/storebridge/', {params})
          this.localStoreData = res.data;
          this.localStores = res.data.data.map((store) => {
            return {
              retailerName: store.attributes.retailer_name,
              retailerAddress: store.attributes.retailer_address,
              retailerLogo: store.attributes.retailer_logo,
              distance: store.attributes.distance || '',
              price: store.attributes.price,
              availability: store.attributes.in_stock,
              shopNowUrl: store.attributes.atc_link || '',
            }
          }) || [];
        } catch(e) {
          this.handleError(e);
        }

        this.isLoading = false;
      }
    },
    watch: {
      variant(newVariant) {
        this.updateVariantInfo(newVariant);
        this.getLocalStoreData(this.localZipcode);
      },
      localZipcode(newZipcode) {
        this.getLocalStoreData(newZipcode);
      }
    },
    mounted() {
      this.getLocalZipcode();
      this.updateVariantInfo(this.variant || this.product.variants[0]);
      this.getLocalStoreData(this.localZipcode);
    }
  }
</script>

